/**
 * Date and time formatting utilities
 * Provides consistent date/time formatting across the application
 * Supports timezone conversion and user preference-based formatting
 */

import { DATE_FORMATS, TIME_FORMATS } from '../constants/appConstants';

/**
 * Gets the default timezone (IST for India, or browser timezone)
 * @returns {string} - IANA timezone identifier
 */
const getDefaultTimezone = () => {
  // Default to IST (India Standard Time) for Indian context
  // Can be overridden by user preferences in the future
  return 'Asia/Kolkata';
};

/**
 * Formats a date string according to the specified format with timezone support
 * @param {string} dateString - Date string to format
 * @param {string} dateFormat - Format pattern (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * @param {string} timezone - IANA timezone identifier (defaults to IST)
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatDate = (dateString, dateFormat, timezone = null) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // Use specified timezone or default
    const tz = timezone || getDefaultTimezone();
    
    // Convert to target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const parts = formatter.formatToParts(date);
    const day = parts.find(p => p.type === 'day').value;
    const month = parts.find(p => p.type === 'month').value;
    const year = parts.find(p => p.type === 'year').value;

    switch (dateFormat) {
      case DATE_FORMATS.DD_MM_YYYY:
        return `${day}/${month}/${year}`;
      case DATE_FORMATS.MM_DD_YYYY:
        return `${month}/${day}/${year}`;
      case DATE_FORMATS.YYYY_MM_DD:
        return `${year}-${month}-${day}`;
      default:
        return `${year}-${month}-${day}`;
    }
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Formats a time string according to the specified format with timezone support
 * @param {string} timeString - Time string to format (HH:MM format or ISO datetime)
 * @param {string} timeFormat - Format pattern (12-hour or 24-hour)
 * @param {string} timezone - IANA timezone identifier (defaults to IST)
 * @returns {string} - Formatted time string or 'N/A' if invalid
 */
export const formatTime = (timeString, timeFormat, timezone = null) => {
  if (!timeString) return 'N/A';
  
  try {
    const tz = timezone || getDefaultTimezone();
    let date;
    
    // Check if it's a full datetime string or just time
    if (timeString.includes('T') || timeString.includes(' ')) {
      date = new Date(timeString);
    } else {
      // Assume it's HH:MM format
      const [hours, minutes] = timeString.split(':');
      if (!hours || !minutes) return 'N/A';
      date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
    }
    
    if (isNaN(date.getTime())) return 'N/A';

    return date.toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: timeFormat === TIME_FORMATS.TWELVE_HOUR
    });
  } catch (error) {
    console.error('Time formatting error:', error);
    return 'N/A';
  }
};

/**
 * Formats a datetime string with both date and time
 * @param {string} dateTimeString - ISO datetime string to format
 * @param {string} dateFormat - Format pattern for date (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * @param {string} timeFormat - Format pattern for time (12-hour or 24-hour)
 * @param {string} timezone - IANA timezone identifier (defaults to IST)
 * @param {string} separator - Separator between date and time (default: ' ')
 * @returns {string} - Formatted datetime string or 'N/A' if invalid
 */
export const formatDateTime = (dateTimeString, dateFormat, timeFormat, timezone = null, separator = ' ') => {
  if (!dateTimeString) return 'N/A';
  
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return 'N/A';

    const formattedDate = formatDate(dateTimeString, dateFormat, timezone);
    const formattedTime = formatTime(dateTimeString, timeFormat, timezone);
    
    if (formattedDate === 'N/A' || formattedTime === 'N/A') {
      return 'N/A';
    }
    
    return `${formattedDate}${separator}${formattedTime}`;
  } catch (error) {
    console.error('DateTime formatting error:', error);
    return 'N/A';
  }
};

/**
 * Formats a date using locale-aware formatting (for display purposes)
 * @param {string} dateString - Date string to format
 * @param {string} timezone - IANA timezone identifier (defaults to IST)
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatDateLocale = (dateString, timezoneOrOptions = null, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    let tz = null;
    let actualOptions = options;

    if (timezoneOrOptions && typeof timezoneOrOptions === 'object') {
      actualOptions = { ...timezoneOrOptions, ...options };
    } else {
      tz = timezoneOrOptions;
    }

    const finalTz = tz || getDefaultTimezone();
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: finalTz,
      ...actualOptions
    };

    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Locale date formatting error:', error);
    return 'N/A';
  }
};